import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CTAs from "./CTAs";
import { paystackService } from "../services/paystackService";
import { bankDetailsService } from "../services/bankDetailsService";

// Setting up a way to get paid is OPTIONAL. The user can:
//   1. connect Stripe (debtors pay by card via a payment link),
//   2. connect Paystack (SA cards & EFT) — currently shown as "Coming soon", or
//   3. just add their bank details, so debtors pay them directly by bank transfer,
// or skip entirely and do it later.

// Paystack is not available yet: the option is greyed out and can't be selected.
// Flip this to false to bring the Paystack connect flow back.
const PAYSTACK_COMING_SOON = true;

const EMPTY_BANK = { companyName: "", bankName: "", accountNo: "", sortCode: "", iban: "" };

const hasBankDetails = (d) => !!(d && d.companyName && (d.accountNo || d.iban));

const maskAccount = (value) => {
  const v = String(value || "").replace(/\s+/g, "");
  return v.length > 4 ? `•••• ${v.slice(-4)}` : v;
};

const UpdatePayment = ({
  onClose,
  onConnect,
  onStripeDisconnect,
  stripeAccountStatus,
  paystackAccountStatus,
  onPaystackConnected,
  onPaystackDisconnect,
  onBankDetailsSaved,
  // Called when the user is ready to start a Pre-LBA (e.g. open the invoice
  // picker). If not passed, the modal navigates to continuePath instead.
  onContinue,
  continuePath = "/lbaform",
  // Invoice data handed to LbaForm via router state when navigating to
  // continuePath (name, businessName, invoiceNumber, amount, currency, email,
  // dueDate, address fields, source…). LbaForm autopopulates from this.
  continueState,
}) => {
  const navigate = useNavigate();

  const handleContinueToPreLba = () => {
    onClose?.();
    if (onContinue) onContinue();
    else navigate(continuePath, { state: continueState });
  };

  // Default: whichever provider is active, else Stripe (bank tab switches in
  // automatically below if bank details are already saved and no provider is set).
  // Paystack is never the default while it is marked as coming soon.
  const [selectedProvider, setSelectedProvider] = useState(
    !PAYSTACK_COMING_SOON && paystackAccountStatus?.isActiveProvider ? "paystack" : "stripe"
  );

  // ── Paystack connect form state ──
  const [banks, setBanks]               = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [businessName, setBusinessName] = useState(paystackAccountStatus?.businessName || "");
  const [bankCode, setBankCode]         = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [resolvedName, setResolvedName] = useState("");
  const [resolving, setResolving]       = useState(false);
  const [connecting, setConnecting]     = useState(false);
  const [error, setError]               = useState("");

  // Only show the connect form if not already connected, or the user explicitly wants to reconnect/edit
  const [showPaystackForm, setShowPaystackForm] = useState(!paystackAccountStatus?.connected);

  // ── Disconnect flow ──
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [disconnecting, setDisconnecting]               = useState(false);
  const [disconnectError, setDisconnectError]           = useState("");

  const [confirmingStripeDisconnect, setConfirmingStripeDisconnect] = useState(false);
  const [disconnectingStripe, setDisconnectingStripe]               = useState(false);
  const [stripeDisconnectError, setStripeDisconnectError]           = useState("");

  // ── Bank details (direct bank transfer) ──
  const [bankForm, setBankForm]           = useState(EMPTY_BANK);
  const [savedBank, setSavedBank]         = useState(null);
  const [loadingBank, setLoadingBank]     = useState(true);
  const [showBankForm, setShowBankForm]   = useState(true);
  const [savingBank, setSavingBank]       = useState(false);
  const [bankError, setBankError]         = useState("");
  const [bankSavedMsg, setBankSavedMsg]   = useState("");

  const stripeReady =
    stripeAccountStatus?.connected && stripeAccountStatus?.onboardingComplete;

  // Load any saved bank details once
  useEffect(() => {
    let cancelled = false;
    bankDetailsService
      .get()
      .then((details) => {
        if (cancelled) return;
        const merged = { ...EMPTY_BANK, ...(details || {}) };
        setBankForm(merged);
        if (hasBankDetails(merged)) {
          setSavedBank(merged);
          setShowBankForm(false);
          // No usable provider connected but bank details saved → open on the bank tab
          const paystackUsable = !PAYSTACK_COMING_SOON && paystackAccountStatus?.connected;
          if (!stripeReady && !paystackUsable) setSelectedProvider("bank");
        }
      })
      .catch(() => { /* not fatal — form just starts empty */ })
      .finally(() => !cancelled && setLoadingBank(false));
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If Paystack becomes disconnected out from under this modal (e.g. after a
  // successful disconnect refreshes paystackAccountStatus), fall back to the
  // connect form instead of continuing to show the "already connected" panel.
  useEffect(() => {
    if (!paystackAccountStatus?.connected) {
      setShowPaystackForm(true);
      setConfirmingDisconnect(false);
    }
  }, [paystackAccountStatus?.connected]);

  const handleStripeDisconnect = async () => {
    setDisconnectingStripe(true); setStripeDisconnectError("");
    try {
      await onStripeDisconnect?.();
      setConfirmingStripeDisconnect(false);
    } catch (e) {
      setStripeDisconnectError(e.message || "Failed to disconnect Stripe account.");
    } finally {
      setDisconnectingStripe(false);
    }
  };

  useEffect(() => {
    if (PAYSTACK_COMING_SOON) return;
    if (selectedProvider === "paystack" && showPaystackForm && banks.length === 0) {
      setLoadingBanks(true);
      paystackService.listBanks()
        .then(setBanks)
        .catch(() => setError("Failed to load bank list."))
        .finally(() => setLoadingBanks(false));
    }
  }, [selectedProvider, showPaystackForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResolve = async () => {
    if (!bankCode || !accountNumber) {
      setError("Please select a bank and enter an account number.");
      return;
    }
    setResolving(true); setError(""); setResolvedName("");
    try {
      const name = await paystackService.resolveAccount(accountNumber, bankCode);
      setResolvedName(name);
    } catch (e) {
      setError(e.message || "Could not verify account. You can still connect, but double-check the details.");
    } finally {
      setResolving(false);
    }
  };

  const handlePaystackConnect = async () => {
    if (!businessName || !bankCode || !accountNumber) {
      setError("Please fill in business name, bank, and account number.");
      return;
    }
    setConnecting(true); setError("");
    try {
      await paystackService.createSubaccount({ businessName, bankCode, accountNumber });
      onPaystackConnected?.();
      onClose();
    } catch (e) {
      setError(e.message || "Failed to connect Paystack account.");
    } finally {
      setConnecting(false);
    }
  };

  // When already connected, "continuing" just proceeds with the existing subaccount
  // (mirrors Stripe's "Open Dashboard" behavior) instead of re-running the connect flow.
  const handlePaystackContinue = () => {
    onPaystackConnected?.();
    handleContinueToPreLba();
  };

  const handlePaystackDisconnect = async () => {
    setDisconnecting(true); setDisconnectError("");
    try {
      await onPaystackDisconnect?.();
      // Stay in the modal — the status-changed effect above will flip this
      // panel over to the connect form so the user can link a different account.
      setConfirmingDisconnect(false);
    } catch (e) {
      setDisconnectError(e.message || "Failed to disconnect Paystack account.");
    } finally {
      setDisconnecting(false);
    }
  };

  const getStripeButtonText = () => {
    if (!stripeAccountStatus?.connected) return "Connect Stripe";
    if (!stripeAccountStatus?.onboardingComplete) return "Complete Setup";
    return "Open Dashboard";
  };

  const handleStripeConnect = async () => {
    if (onConnect) await onConnect();
  };

  // ── Bank details handlers ──
  const setBankField = (field) => (e) => {
    setBankForm((prev) => ({ ...prev, [field]: e.target.value }));
    setBankError("");
    setBankSavedMsg("");
  };

  const handleSaveBank = async () => {
    const details = {
      companyName: bankForm.companyName.trim(),
      bankName:    bankForm.bankName.trim(),
      accountNo:   bankForm.accountNo.replace(/\s+/g, ""),
      sortCode:    bankForm.sortCode.replace(/[\s-]+/g, ""),
      iban:        bankForm.iban.replace(/\s+/g, "").toUpperCase(),
    };

    if (!details.companyName) {
      setBankError("Enter the name on the account.");
      return;
    }
    if (!details.accountNo && !details.iban) {
      setBankError("Enter an account number (or an IBAN).");
      return;
    }
    if (details.sortCode && !/^\d{6}$/.test(details.sortCode)) {
      setBankError("Sort code should be 6 digits, e.g. 12-34-56.");
      return;
    }

    setSavingBank(true); setBankError("");
    try {
      const saved = await bankDetailsService.save(details);
      const merged = { ...EMPTY_BANK, ...(saved || details) };
      setSavedBank(merged);
      setBankForm(merged);
      setShowBankForm(false);
      setBankSavedMsg("Bank details saved. Debtors will be asked to pay into this account. You can start your Pre-LBA now.");
      onBankDetailsSaved?.(merged);
    } catch (e) {
      setBankError(e.message || "Couldn't save your bank details. Please try again.");
    } finally {
      setSavingBank(false);
    }
  };

  const formatSortCode = (v) => {
    const d = String(v || "").replace(/\D/g, "");
    return d.length === 6 ? `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}` : v;
  };

  return (
    <>
      <style>{`
        .dd-card { background:#fff; border:1px solid #E2E8F0; border-radius:16px; box-shadow:0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04); font-family:'DM Sans',sans-serif; }
        .dd-provider-option { display:flex; align-items:center; gap:10px; padding:12px 14px; border:1.5px solid #E2E8F0; border-radius:10px; cursor:pointer; transition:border-color .15s, background .15s; background:#fff; text-align:left; width:100%; font-family:'DM Sans',sans-serif; }
        .dd-provider-option:hover { border-color:#93C5FD; background:#F8FAFC; }
        .dd-provider-option.selected { border-color:#2563EB; background:#EFF6FF; }
        .dd-provider-option:focus-visible { outline:2px solid #2563EB; outline-offset:2px; }
        .dd-provider-option:disabled,
        .dd-provider-option:disabled:hover { cursor:not-allowed; background:#F8FAFC; border-color:#E2E8F0; }
        .dd-provider-option:disabled .dd-provider-text { opacity:.5; }
        .dd-active-badge { font-size:11px; font-weight:700; color:#166534; background:#F0FDF4; border:1px solid #BBF7D0; border-radius:20px; padding:2px 8px; white-space:nowrap; }
        .dd-soon-badge { font-size:11px; font-weight:700; color:#64748B; background:#F1F5F9; border:1px solid #E2E8F0; border-radius:20px; padding:2px 8px; white-space:nowrap; }
        .dd-input { width:100%; padding:10px 14px; border:1px solid #E2E8F0; border-radius:10px; font-family:'DM Sans',sans-serif; font-size:14px; color:#0F172A; outline:none; box-sizing:border-box; }
        .dd-input:focus { border-color:#2563EB; box-shadow:0 0 0 3px rgba(37,99,235,.1); }
        .dd-field-label { font-size:12px; font-weight:600; color:#64748B; margin-bottom:6px; }
        .dd-field-hint { font-size:11px; color:#94A3B8; margin-top:4px; }
        .dd-link-btn { background:none; border:none; padding:0; font-size:12px; font-weight:600; color:#2563EB; cursor:pointer; text-decoration:underline; font-family:'DM Sans',sans-serif; }
        .dd-link-btn.danger { color:#DC2626; }
        .dd-disconnect-confirm { background:#FEF2F2; border:1px solid #FECACA; border-radius:10px; padding:12px 14px; display:flex; flex-direction:column; gap:10px; }
        .dd-bank-summary { border:1px solid #E2E8F0; border-radius:10px; padding:12px 14px; display:grid; grid-template-columns:auto 1fr; gap:6px 16px; font-size:13px; }
        .dd-bank-summary dt { color:#64748B; margin:0; }
        .dd-bank-summary dd { color:#0F172A; font-weight:600; margin:0; text-align:right; }
      `}</style>

      <div className="dd-card" style={{ width: 440, maxWidth: "calc(100vw - 32px)", maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* Header + provider picker */}
        <div style={{ padding: "24px 24px 0", display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>How should debtors pay you?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4, lineHeight: 1.5 }}>
              Optional. You can create Pre-LBAs without this and set it up later.{" "}
              <button type="button" className="dd-link-btn" style={{ fontSize: 13 }} onClick={handleContinueToPreLba}>
                Continue without it
              </button>
            </div>
          </div>

          <button
            type="button"
            className={`dd-provider-option${selectedProvider === "stripe" ? " selected" : ""}`}
            onClick={() => setSelectedProvider("stripe")}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>Stripe</div>
              <div style={{ fontSize: 11, color: "#94A3B8" }}>Debtors pay by card through a payment link</div>
            </div>
            {stripeAccountStatus?.isActiveProvider && <span className="dd-active-badge">Active</span>}
          </button>

          <button
            type="button"
            className={`dd-provider-option${!PAYSTACK_COMING_SOON && selectedProvider === "paystack" ? " selected" : ""}`}
            onClick={() => { if (!PAYSTACK_COMING_SOON) setSelectedProvider("paystack"); }}
            disabled={PAYSTACK_COMING_SOON}
            aria-disabled={PAYSTACK_COMING_SOON}
          >
            <div className="dd-provider-text" style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>Paystack</div>
              <div style={{ fontSize: 11, color: "#94A3B8" }}>South African cards & EFT</div>
            </div>
            {PAYSTACK_COMING_SOON ? (
              <span className="dd-soon-badge">Coming soon</span>
            ) : (
              paystackAccountStatus?.isActiveProvider && <span className="dd-active-badge">Active</span>
            )}
          </button>

          <button
            type="button"
            className={`dd-provider-option${selectedProvider === "bank" ? " selected" : ""}`}
            onClick={() => setSelectedProvider("bank")}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>Bank transfer</div>
              <div style={{ fontSize: 11, color: "#94A3B8" }}>No account needed. Debtors pay straight into your bank</div>
            </div>
            {savedBank && <span className="dd-active-badge">Saved</span>}
          </button>
        </div>

        {/* ── Stripe panel ── */}
        {selectedProvider === "stripe" && (
          <>
            <div style={{ padding: "20px 24px 0", display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 14, color: "#64748B", lineHeight: 1.5 }}>
                {stripeReady
                  ? "Your Stripe account is connected. Debtors can pay by card and the money goes to your Stripe account."
                  : "Connect a Stripe account if you want debtors to pay by card. Prefer not to? Choose Bank transfer instead."}
              </div>
            </div>

            {stripeReady && (
              <div style={{ padding: "16px 24px 0" }}>
                {!confirmingStripeDisconnect ? (
                  <button type="button" className="dd-link-btn danger" onClick={() => setConfirmingStripeDisconnect(true)}>
                    Disconnect Stripe
                  </button>
                ) : (
                  <div className="dd-disconnect-confirm">
                    <div style={{ fontSize: 13, color: "#991B1B", lineHeight: 1.5 }}>
                      Disconnecting removes this Stripe account from your company. Any Pre-LBAs already sent with a Stripe payment link will keep working, but you won't be able to create new Stripe payment links until you reconnect.
                    </div>
                    {stripeDisconnectError && <div style={{ fontSize: 12, color: "#DC2626" }}>{stripeDisconnectError}</div>}
                    <div style={{ display: "flex", gap: 10 }}>
                      <CTAs
                        type="Secondarybtn"
                        text="Keep connected"
                        width="50%"
                        onClick={() => { setConfirmingStripeDisconnect(false); setStripeDisconnectError(""); }}
                        disabled={disconnectingStripe}
                      />
                      <CTAs
                        type="Signinbtn"
                        text={disconnectingStripe ? "Disconnecting…" : "Yes, disconnect"}
                        width="50%"
                        onClick={handleStripeDisconnect}
                        disabled={disconnectingStripe}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", padding: 24, gap: 12 }}>
              <CTAs
                type="Secondarybtn"
                text={stripeReady ? "Close" : "Skip for now"}
                width="50%"
                onClick={onClose}
                disabled={disconnectingStripe}
              />
              <CTAs type="Signinbtn" text={getStripeButtonText()} width="50%" onClick={handleStripeConnect} disabled={disconnectingStripe} />
            </div>
            {stripeReady && (
              <div style={{ padding: "0 24px 24px", marginTop: -12 }}>
                <CTAs type="Signinbtn" text="Continue to Pre-LBA" width="100%" onClick={handleContinueToPreLba} disabled={disconnectingStripe} />
              </div>
            )}
          </>
        )}

        {/* ── Paystack panel (hidden while Paystack is marked as coming soon) ── */}
        {!PAYSTACK_COMING_SOON && selectedProvider === "paystack" && (
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            {paystackAccountStatus?.connected && !showPaystackForm ? (
              <>
                <div style={{ fontSize: 13, color: "#166534", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "10px 14px" }}>
                  Already connected as <strong>{paystackAccountStatus.businessName}</strong>.
                  {!paystackAccountStatus.isActiveProvider && " Continuing will set Paystack as your active provider."}
                </div>
                <div style={{ fontSize: 14, color: "#64748B", lineHeight: 1.5 }}>
                  Your Paystack subaccount is connected and ready to accept payments.
                </div>

                {!confirmingDisconnect ? (
                  <div style={{ display: "flex", gap: 16 }}>
                    <button type="button" className="dd-link-btn" onClick={() => setShowPaystackForm(true)}>
                      Edit bank details
                    </button>
                    <button type="button" className="dd-link-btn danger" onClick={() => setConfirmingDisconnect(true)}>
                      Disconnect Paystack
                    </button>
                  </div>
                ) : (
                  <div className="dd-disconnect-confirm">
                    <div style={{ fontSize: 13, color: "#991B1B", lineHeight: 1.5 }}>
                      Disconnecting removes this Paystack account from your company. Any Pre-LBAs already sent with a Paystack payment link will keep working, but you won't be able to create new Paystack payment links until you reconnect.
                    </div>
                    {disconnectError && <div style={{ fontSize: 12, color: "#DC2626" }}>{disconnectError}</div>}
                    <div style={{ display: "flex", gap: 10 }}>
                      <CTAs
                        type="Secondarybtn"
                        text="Keep connected"
                        width="50%"
                        onClick={() => { setConfirmingDisconnect(false); setDisconnectError(""); }}
                        disabled={disconnecting}
                      />
                      <CTAs
                        type="Signinbtn"
                        text={disconnecting ? "Disconnecting…" : "Yes, disconnect"}
                        width="50%"
                        onClick={handlePaystackDisconnect}
                        disabled={disconnecting}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 8 }}>
                  <CTAs type="Secondarybtn" text="Close" width="50%" onClick={onClose} disabled={disconnecting} />
                  <CTAs type="Signinbtn" text="Continue to Pre-LBA" width="50%" onClick={handlePaystackContinue} disabled={disconnecting} />
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5 }}>
                  {paystackAccountStatus?.connected
                    ? "Update your business and bank details below to reconnect."
                    : "Paystack has no hosted onboarding. Enter your business and bank details below to connect."}
                </div>

                <div>
                  <div className="dd-field-label">Business name</div>
                  <input className="dd-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Your business name" />
                </div>

                <div>
                  <div className="dd-field-label">Bank</div>
                  <select className="dd-input" value={bankCode} onChange={(e) => { setBankCode(e.target.value); setResolvedName(""); }} disabled={loadingBanks}>
                    <option value="">{loadingBanks ? "Loading banks…" : "Select your bank"}</option>
                    {banks.map((b) => (
                      <option key={b.code} value={b.code}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="dd-field-label">Account number</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      className="dd-input"
                      value={accountNumber}
                      onChange={(e) => { setAccountNumber(e.target.value); setResolvedName(""); }}
                      placeholder="Account number"
                    />
                    <button
                      type="button"
                      onClick={handleResolve}
                      disabled={resolving || !bankCode || !accountNumber}
                      style={{ padding: "0 16px", borderRadius: 10, border: "1px solid #E2E8F0", background: "#fff", color: "#2563EB", fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {resolving ? "Checking…" : "Verify"}
                    </button>
                  </div>
                  {resolvedName && (
                    <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>✓ Account holder: <strong>{resolvedName}</strong></div>
                  )}
                </div>

                {error && <div style={{ fontSize: 13, color: "#DC2626" }}>{error}</div>}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 8 }}>
                  <CTAs
                    type="Secondarybtn"
                    text={paystackAccountStatus?.connected ? "Back" : "Skip for now"}
                    width="50%"
                    onClick={paystackAccountStatus?.connected ? () => setShowPaystackForm(false) : onClose}
                  />
                  <CTAs
                    type="Signinbtn"
                    text={connecting ? "Connecting…" : "Connect Paystack"}
                    width="50%"
                    onClick={handlePaystackConnect}
                    disabled={connecting}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Bank transfer panel ── */}
        {selectedProvider === "bank" && (
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5 }}>
              These details are shown to debtors so they can pay you directly. 6ELA never holds the money.
            </div>

            {loadingBank ? (
              <div style={{ fontSize: 13, color: "#64748B" }}>Loading your bank details…</div>
            ) : savedBank && !showBankForm ? (
              <>
                <dl className="dd-bank-summary" style={{ margin: 0 }}>
                  <dt>Account name</dt><dd>{savedBank.companyName}</dd>
                  {savedBank.bankName && (<><dt>Bank</dt><dd>{savedBank.bankName}</dd></>)}
                  {savedBank.sortCode && (<><dt>Sort code</dt><dd>{formatSortCode(savedBank.sortCode)}</dd></>)}
                  {savedBank.accountNo && (<><dt>Account number</dt><dd>{maskAccount(savedBank.accountNo)}</dd></>)}
                  {savedBank.iban && (<><dt>IBAN</dt><dd>{maskAccount(savedBank.iban)}</dd></>)}
                </dl>

                {bankSavedMsg && (
                  <div role="status" style={{ fontSize: 13, color: "#166534", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "10px 14px" }}>
                    {bankSavedMsg}
                  </div>
                )}

                <div>
                  <button type="button" className="dd-link-btn" onClick={() => { setShowBankForm(true); setBankSavedMsg(""); }}>
                    Edit bank details
                  </button>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 8 }}>
                  <CTAs type="Secondarybtn" text="Close" width="50%" onClick={onClose} />
                  <CTAs type="Signinbtn" text="Continue to Pre-LBA" width="50%" onClick={handleContinueToPreLba} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="dd-field-label">Account name</div>
                  <input className="dd-input" value={bankForm.companyName} onChange={setBankField("companyName")} placeholder="Name on the bank account" autoComplete="organization" />
                </div>

                <div>
                  <div className="dd-field-label">Bank name <span style={{ fontWeight: 400, color: "#94A3B8" }}>(optional)</span></div>
                  <input className="dd-input" value={bankForm.bankName} onChange={setBankField("bankName")} placeholder="e.g. Barclays" />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 10 }}>
                  <div>
                    <div className="dd-field-label">Sort code</div>
                    <input className="dd-input" value={bankForm.sortCode} onChange={setBankField("sortCode")} placeholder="12-34-56" inputMode="numeric" maxLength={8} />
                  </div>
                  <div>
                    <div className="dd-field-label">Account number</div>
                    <input className="dd-input" value={bankForm.accountNo} onChange={setBankField("accountNo")} placeholder="12345678" inputMode="numeric" />
                  </div>
                </div>

                <div>
                  <div className="dd-field-label">IBAN <span style={{ fontWeight: 400, color: "#94A3B8" }}>(optional)</span></div>
                  <input className="dd-input" value={bankForm.iban} onChange={setBankField("iban")} placeholder="For international payments" />
                  <div className="dd-field-hint">Outside the UK? Leave the sort code empty and use your account number or IBAN.</div>
                </div>

                {bankError && <div role="alert" style={{ fontSize: 13, color: "#DC2626" }}>{bankError}</div>}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 8 }}>
                  <CTAs
                    type="Secondarybtn"
                    text={savedBank ? "Back" : "Skip for now"}
                    width="50%"
                    onClick={savedBank ? () => { setBankForm(savedBank); setShowBankForm(false); setBankError(""); } : onClose}
                    disabled={savingBank}
                  />
                  <CTAs
                    type="Signinbtn"
                    text={savingBank ? "Saving…" : "Save bank details"}
                    width="50%"
                    onClick={handleSaveBank}
                    disabled={savingBank}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default UpdatePayment;